import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileUp, X } from "lucide-react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { DsButton } from "../../../design-system";
import { salaryStructureService } from "../../../services/api";
import {
  ImportValidationReview,
  buildImportValidationResult,
  downloadValidationStatusWorkbook,
} from "../../Common/import-validation";
import { structureAppliesToEmployee } from "../../../utils/salaryStructureMatch";
import {
  listManualFixedComponents,
  manualFixedColumnLabel,
  buildEmployeeStructureSavePayload,
} from "../../../utils/applyEmployeeStructureFromTemplate";
import { getAuthJson } from "../../../utils/authStorage";

export const EXCEL_DATE_FORMAT = "dd/mm/yyyy";

const TEMPLATE_SCOPES = {
  not_assigned: "not_assigned",
  all_under: "all_under",
};

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function parseNumber(value) {
  if (value == null || value === "") return NaN;
  return Number(String(value).replace(/,/g, "").trim());
}

function parseExcelDate(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    // Prefer local Y-M-D (Excel/JS date cells). Avoid toISOString UTC day-shift in IST.
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (/^\d+(\.\d+)?$/.test(s) && Number(s) > 20000 && Number(s) < 80000) {
    const serial = Math.floor(Number(s));
    const utc = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
    const y = utc.getUTCFullYear();
    const m = String(utc.getUTCMonth() + 1).padStart(2, "0");
    const d = String(utc.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${String(dmy[2]).padStart(2, "0")}-${String(dmy[1]).padStart(2, "0")}`;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  return s;
}

/** ISO YYYY-MM-DD or date-like → dd/mm/yyyy for Excel seed */
function toExcelDmy(value) {
  const iso = parseExcelDate(value);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function employeeJoiningDate(emp) {
  return (
    emp?.joiningDate ||
    emp?.dateOfJoining ||
    emp?.dateOfJoin ||
    emp?.doj ||
    emp?.joining_date ||
    null
  );
}

function employeeDisplayName(emp) {
  return (
    [emp?.user?.name, emp?.user?.lastName, emp?.firstName, emp?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    emp?.name ||
    ""
  );
}

function isActiveEmployment(emp) {
  const s = String(emp?.employmentStatus || "").trim();
  return s !== "Relieved" && s !== "Terminated";
}

/**
 * Bulk Manual component values import for a salary structure.
 * Employee CTC (profile) wins; Formula / Balance CTC stay derived.
 */
export default function StructureComponentImportModal({
  isOpen,
  onClose,
  darkMode = false,
  employees = [],
  structures = [],
  statutoryConfigJson = null,
  branchesList = [],
  onImported,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [templateScope, setTemplateScope] = useState(TEMPLATE_SCOPES.not_assigned);
  const [activeMap, setActiveMap] = useState([]);
  const [sourceFile, setSourceFile] = useState(null);
  const [review, setReview] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const selectedStructure = useMemo(
    () => (structures || []).find((s) => String(s.id) === String(templateId)) || null,
    [structures, templateId]
  );

  const manualComponents = useMemo(
    () => listManualFixedComponents(selectedStructure),
    [selectedStructure]
  );

  const componentHeaders = useMemo(
    () => manualComponents.map((c) => manualFixedColumnLabel(c)),
    [manualComponents]
  );

  const fixedHeaders = useMemo(
    () => [
      "Employee Code*",
      "Employee Name",
      "Current CTC",
      ...componentHeaders,
      "Effective From*",
    ],
    [componentHeaders]
  );

  const employeesByCode = useMemo(() => {
    const map = new Map();
    (employees || []).forEach((emp) => {
      const code = String(emp.empCode || emp.employeeCode || "")
        .trim()
        .toUpperCase();
      if (code) map.set(code, emp);
    });
    return map;
  }, [employees]);

  const assignedTemplateIdsByEmployee = useMemo(() => {
    const map = new Map();
    (activeMap || []).forEach((row) => {
      map.set(Number(row.employeeId), Number(row.templateId));
    });
    return map;
  }, [activeMap]);

  const seedEmployees = useMemo(() => {
    if (!selectedStructure) return [];
    const matched = (employees || []).filter((emp) => {
      if (!isActiveEmployment(emp)) return false;
      return structureAppliesToEmployee(selectedStructure.assignment || {}, emp);
    });
    if (templateScope === TEMPLATE_SCOPES.all_under) {
      return matched.filter(
        (emp) =>
          Number(assignedTemplateIdsByEmployee.get(Number(emp.id))) ===
          Number(selectedStructure.id)
      );
    }
    // Default: matched but no active structure for this template (or none at all)
    return matched.filter((emp) => {
      const tid = assignedTemplateIdsByEmployee.get(Number(emp.id));
      return tid == null || Number(tid) !== Number(selectedStructure.id);
    });
  }, [
    employees,
    selectedStructure,
    templateScope,
    assignedTemplateIdsByEmployee,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await salaryStructureService.listActiveEmployeeStructureMap(
          templateId || null
        );
        if (!cancelled) setActiveMap(Array.isArray(res?.data) ? res.data : []);
      } catch {
        if (!cancelled) setActiveMap([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, templateId]);

  useEffect(() => {
    if (!isOpen) {
      setReview(null);
      setImportResult(null);
      setSourceFile(null);
      setTemplateId("");
      setTemplateScope(TEMPLATE_SCOPES.not_assigned);
    }
  }, [isOpen]);

  if (!isOpen || typeof document === "undefined") return null;

  const downloadTemplate = async () => {
    if (!selectedStructure) {
      toast.error("Select a salary structure first");
      return;
    }
    if (!manualComponents.length) {
      toast.error(
        "This structure has no Manual fixed components to import. Use Formula/Assign instead."
      );
      return;
    }
    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = "PaceHRM";
      wb.created = new Date();

      const info = wb.addWorksheet("Instructions");
      info.getColumn(1).width = 100;
      const lines = [
        "Salary Structure — Manual Component Values Import",
        "",
        `Structure: ${selectedStructure.name}`,
        `Scope: ${
          templateScope === TEMPLATE_SCOPES.all_under
            ? "All employees currently under this structure"
            : "Not assigned (matched to structure, no active snapshot for it)"
        }`,
        "",
        "1. Use the Components sheet only. Do not rename headers.",
        "2. Employee Code must match PaceHRM exactly.",
        "3. Current CTC is read-only reference from the employee profile (Org Details). Imports do NOT change CTC.",
        "4. Fill monthly amounts only for Manual fixed components listed as columns.",
        "5. Do not enter Formula or Balance CTC amounts — those are calculated to fit Profile CTC / Gross.",
        "6. Blank Manual amount = keep structure template default (create) / existing value (update).",
        `7. Effective From* format: ${EXCEL_DATE_FORMAT}. For new joiners use joining date (pre-filled). Server pins first map to DOJ; after payroll process/finalise use Salary Revision / Import Salary Revisions instead.`,
        "8. Set / revise CTC first if needed (Salary Revision import), then upload Manual amounts.",
        "9. One row per employee. Duplicate Employee Codes are rejected.",
        "10. Save as .xlsx and upload from this screen.",
      ];
      lines.forEach((t, i) => {
        const cell = info.getCell(i + 1, 1);
        cell.value = t;
        if (i === 0) cell.font = { bold: true, size: 14 };
      });

      const sheet = wb.addWorksheet("Components");
      fixedHeaders.forEach((h, i) => {
        const cell = sheet.getCell(1, i + 1);
        cell.value = h;
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE8EEF5" },
        };
        sheet.getColumn(i + 1).width = i === 1 ? 24 : i === fixedHeaders.length - 1 ? 16 : 14;
      });

      const seed = seedEmployees.slice(0, 500);
      const effCol = fixedHeaders.length;
      if (seed.length) {
        seed.forEach((emp, idx) => {
          const r = idx + 2;
          sheet.getCell(r, 1).value = emp.empCode || emp.employeeCode || "";
          sheet.getCell(r, 2).value = employeeDisplayName(emp);
          sheet.getCell(r, 3).value = Number(emp.ctc || emp.ctcAmount || 0) || null;
          manualComponents.forEach((comp, ci) => {
            const def = Number(comp.manualAmount);
            sheet.getCell(r, 4 + ci).value =
              Number.isFinite(def) && def > 0 ? def : null;
          });
          sheet.getCell(r, effCol).value = toExcelDmy(employeeJoiningDate(emp)) || "";
          sheet.getCell(r, effCol).numFmt = "@";
        });
      } else {
        sheet.getCell(2, 1).value = "MK0001";
        sheet.getCell(2, 2).value = "Sample Employee";
        sheet.getCell(2, 3).value = 500000;
        sheet.getCell(2, effCol).value = "15/02/2026";
        sheet.getCell(2, effCol).numFmt = "@";
      }

      for (let r = 2; r <= Math.max(seed.length + 1, 200); r++) {
        sheet.getCell(r, effCol).numFmt = "@";
      }

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Structure_Manual_Components_${String(selectedStructure.name || "Import")
        .replace(/[^\w.-]+/g, "_")
        .slice(0, 40)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        seed.length
          ? `Template downloaded (${seed.length} employee row${seed.length === 1 ? "" : "s"})`
          : "Template downloaded (no matching employees for this scope — add rows manually)"
      );
    } catch (err) {
      toast.error(err?.message || "Failed to download template");
    }
  };

  const mapRow = (headers, raw, rowNumber) => {
    const obj = {};
    headers.forEach((h, i) => {
      obj[normalizeHeader(h)] = raw[i];
    });
    const empCode = String(
      obj["employee code*"] ?? obj["employee code"] ?? ""
    )
      .trim()
      .toUpperCase();
    const employeeName = String(obj["employee name"] ?? "").trim();
    const currentCtcRef = parseNumber(obj["current ctc"]);
    const effectiveFrom = parseExcelDate(
      obj["effective from*"] ?? obj["effective from"] ?? ""
    );

    const overrides = {};
    const amountErrors = [];
    manualComponents.forEach((comp) => {
      const label = manualFixedColumnLabel(comp);
      const key = normalizeHeader(label);
      const rawVal = obj[key];
      if (rawVal == null || String(rawVal).trim() === "") return;
      const n = parseNumber(rawVal);
      if (!Number.isFinite(n) || n < 0) {
        amountErrors.push(`${label} must be a number ≥ 0`);
        return;
      }
      overrides[comp.name] = n;
      if (comp.id != null) overrides[String(comp.id)] = n;
    });

    return {
      rowNumber,
      empCode,
      employeeName,
      currentCtcRef: Number.isFinite(currentCtcRef) ? currentCtcRef : null,
      effectiveFrom,
      overrides,
      amountErrors,
    };
  };

  const validateMappedRows = (mapped) => {
    const validRecords = [];
    const invalidRecords = [];
    const seenCodes = new Map();
    const orgMethod = getAuthJson("companyData")?.defaultSalaryStructureMethod;

    mapped.forEach((row) => {
      const record = {
        "Employee Code": row.empCode,
        "Employee Name": row.employeeName,
        Name: row.employeeName || row.empCode,
        "Current CTC": row.currentCtcRef,
        "Effective From": row.effectiveFrom,
        ...row.overrides,
      };
      const fieldErrors = {};

      if (!row.empCode) fieldErrors["Employee Code"] = "Employee Code is required";
      if (row.empCode) {
        const first = seenCodes.get(row.empCode);
        if (first != null) {
          fieldErrors["Employee Code"] =
            `Duplicated in this file (also on row ${first})`;
        } else {
          seenCodes.set(row.empCode, row.rowNumber);
        }
      }

      const emp = employeesByCode.get(row.empCode);
      if (row.empCode && !emp) {
        fieldErrors["Employee Code"] = "Employee Code not found in this company";
      } else if (emp && !isActiveEmployment(emp)) {
        fieldErrors["Employee Code"] =
          `Employee is ${emp.employmentStatus} — cannot import`;
      }

      if (!row.effectiveFrom || !/^\d{4}-\d{2}-\d{2}$/.test(row.effectiveFrom)) {
        fieldErrors["Effective From"] =
          `Effective From must be ${EXCEL_DATE_FORMAT}`;
      }

      row.amountErrors.forEach((msg, idx) => {
        fieldErrors[`Amount ${idx + 1}`] = msg;
      });

      if (emp && selectedStructure) {
        if (!structureAppliesToEmployee(selectedStructure.assignment || {}, emp)) {
          fieldErrors["Structure"] =
            "Employee is not in this salary structure assignment group";
        }
        const ctc = Number(emp.ctc || emp.ctcAmount || 0);
        if (!ctc || ctc <= 0) {
          fieldErrors["Current CTC"] =
            "Employee CTC is missing — set CTC on Org Details first";
        }

        if (!Object.keys(fieldErrors).length) {
          const built = buildEmployeeStructureSavePayload({
            structure: selectedStructure,
            employee: emp,
            overrides: row.overrides,
            orgSalaryMethod: orgMethod,
            statutoryConfigJson,
            branchesList,
            effectiveFrom: row.effectiveFrom,
            changeNote: `Manual component import: ${selectedStructure.name}`,
          });
          if (!built.ok) {
            fieldErrors.Validation =
              built.message || built.reason || "Structure validation failed";
          } else {
            validRecords.push({
              ...row,
              __row: row.rowNumber,
              __empCode: row.empCode,
              record: {
                ...record,
                __payload: {
                  ...built.payload,
                  __row: row.rowNumber,
                  __empCode: row.empCode,
                },
              },
            });
            return;
          }
        }
      }

      if (Object.keys(fieldErrors).length) {
        invalidRecords.push({
          row: row.rowNumber,
          record,
          error: Object.values(fieldErrors).join("; "),
          fieldErrors,
        });
      }
    });

    return buildImportValidationResult({ validRecords, invalidRecords });
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = "";
    if (!file) return;
    if (!selectedStructure) {
      toast.error("Select a salary structure first");
      return;
    }
    if (!manualComponents.length) {
      toast.error("Selected structure has no Manual fixed components");
      return;
    }

    setBusy(true);
    setImportResult(null);
    setSourceFile(file);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array", cellDates: true });
      const names = workbook.SheetNames || [];
      const sheetName =
        names.find((n) => normalizeHeader(n) === "components") ||
        names.find((n) => normalizeHeader(n).includes("component")) ||
        names[0];
      const worksheet = sheetName ? workbook.Sheets[sheetName] : null;
      const json = worksheet
        ? XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" })
        : [];
      if (!json.length) {
        toast.error("The Excel file appears empty");
        setReview(null);
        return;
      }
      const headers = json[0];
      const mapped = [];
      for (let i = 1; i < json.length; i++) {
        const raw = json[i];
        if (!raw || raw.every((c) => c === "" || c == null)) continue;
        const row = mapRow(headers, raw, i + 1);
        if (!row.empCode && !row.effectiveFrom && !Object.keys(row.overrides).length) {
          continue;
        }
        mapped.push(row);
      }
      if (!mapped.length) {
        toast.error("No data rows found");
        setReview(null);
        return;
      }
      setReview(validateMappedRows(mapped));
    } catch (err) {
      toast.error(err?.message || "Failed to read Excel");
      setReview(null);
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (fromReview) => {
    const rows = fromReview?.successRows || [];
    const payloads = rows
      .map((r) => r.record?.__payload || r.__payload || r.payload)
      .filter((p) => p?.employeeId);
    if (!payloads.length) {
      toast.error("No valid rows to import");
      return;
    }
    setBusy(true);
    try {
      const res = await salaryStructureService.bulkManualImportEmployeeStructures(
        payloads
      );
      const data = res?.data || {};
      setImportResult(data);
      const ok = Number(data.successCount || 0);
      const fail = Number(data.failedCount || 0);
      if (ok) {
        toast.success(
          `Imported ${ok} employee structure${ok === 1 ? "" : "s"}${
            fail ? ` (${fail} failed)` : ""
          }`
        );
        onImported?.();
      } else {
        toast.error(fail ? `All ${fail} rows failed` : "Import failed");
      }
      setReview(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const shellCls = darkMode
    ? "border-gray-700 bg-[#1f1f1f] text-gray-100"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";

  return createPortal(
    <div className="fixed inset-0 z-[10030] flex items-center justify-center bg-black/45 p-4">
      <div
        className={`flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border shadow-xl ${shellCls}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="structure-component-import-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h2 id="structure-component-import-title" className="text-base font-semibold">
              Structure component values
            </h2>
            <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
              Import Manual fixed amounts for Manual salary structures. Profile CTC / Gross stays the source of truth;
              Formula / Balance CTC stay calculated. Required after employee import when the matched structure is Manual.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[hsl(var(--ds-border))]"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs font-medium">
              Salary structure *
              <select
                value={templateId}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  setReview(null);
                  setImportResult(null);
                }}
                className="mt-1 h-9 w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 text-sm"
              >
                <option value="">Select structure…</option>
                {(structures || [])
                  .filter((s) => s.active !== false)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {listManualFixedComponents(s).length
                        ? ""
                        : " (no Manual fixed)"}
                    </option>
                  ))}
              </select>
            </label>
            <label className="block text-xs font-medium">
              Template employees
              <select
                value={templateScope}
                onChange={(e) => setTemplateScope(e.target.value)}
                className="mt-1 h-9 w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 text-sm"
              >
                <option value={TEMPLATE_SCOPES.not_assigned}>
                  Not assigned (default)
                </option>
                <option value={TEMPLATE_SCOPES.all_under}>
                  All under this structure
                </option>
              </select>
            </label>
          </div>

          {selectedStructure ? (
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
              Manual fixed columns:{" "}
              {componentHeaders.length
                ? componentHeaders.join(", ")
                : "none — choose a structure with Manual components"}
              {" · "}
              Seed rows: {seedEmployees.length}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              disabled={busy || !selectedStructure}
              onClick={downloadTemplate}
            >
              <Download size={14} />
              Download template
            </DsButton>
            <DsButton
              type="button"
              variant="primary"
              size="sm"
              disabled={busy || !selectedStructure || !manualComponents.length}
              onClick={() => fileRef.current?.click()}
            >
              <FileUp size={14} />
              Upload Excel
            </DsButton>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleFile}
            />
          </div>

          {review ? (
            <ImportValidationReview
              result={review}
              busy={busy}
              entityLabel="employee"
              onImportAll={() => runImport(review)}
              onImportSuccessfulOnly={() => runImport(review)}
              onDownloadReport={async () => {
                try {
                  const rows = [
                    ...(review.successRows || []),
                    ...(review.failedRows || []),
                  ];
                  await downloadValidationStatusWorkbook(
                    sourceFile,
                    rows,
                    "Structure_Manual_Components_Validation.xlsx",
                    { sheetName: "Components" }
                  );
                } catch (err) {
                  toast.error(err?.message || "Failed to download report");
                }
              }}
              onBack={() => setReview(null)}
            />
          ) : null}

          {importResult ? (
            <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 px-3 py-2 text-xs">
              Done: {importResult.successCount || 0} saved,{" "}
              {importResult.failedCount || 0} failed
              {(importResult.results || [])
                .filter((r) => !r.ok)
                .slice(0, 5)
                .map((r) => (
                  <div key={`${r.row}-${r.empCode}`} className="mt-1 text-[hsl(var(--ds-danger))]">
                    Row {r.row} {r.empCode}: {r.error}
                  </div>
                ))}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <DsButton type="button" variant="outline" size="sm" onClick={onClose}>
            Close
          </DsButton>
        </div>
      </div>
    </div>,
    document.body
  );
}
